/**
 * FikoYT CAD Real-Time Collaboration - TypeScript Types
 */

// ============================================================================
// User Types
// ============================================================================

export interface CollabUser {
    userId: string;
    userName: string;
    userColor: string;  // Hex color e.g. "#FF5722"
    socketId?: string;
    isActive: boolean;
    joinedAt?: number;
    lastSeen?: number;
}

// ============================================================================
// Room Types
// ============================================================================

export interface CollabRoom {
    roomId: string;
    projectId: string;
    users: CollabUser[];
    markups: CollabMarkupData[];
}

export interface RoomJoinData {
    roomId: string;
    projectId: string;
    userId: string;
    userName: string;
    userColor?: string;
}

export interface RoomJoinedData {
    roomId: string;
    users: CollabUser[];
    markups: CollabMarkupData[];
}

export interface RoomUserJoinedData {
    user: CollabUser;
}

export interface RoomUserLeftData {
    userId: string;
}

// ============================================================================
// Cursor Types
// ============================================================================

export interface CollabCursorData {
    userId: string;
    userName?: string;
    userColor?: string;
    rayOrigin: [number, number, number];
    rayDirection: [number, number, number];
    worldPosition?: [number, number, number];
    hoveredUuid?: string;
    timestamp: number;
    hidden?: boolean;
}

export interface CursorMoveData {
    roomId: string;
    userId: string;
    userName: string;
    userColor: string;
    rayOrigin: [number, number, number];
    rayDirection: [number, number, number];
    worldPosition?: [number, number, number];
    hoveredUuid?: string;
}

// ============================================================================
// Avatar Types
// ============================================================================

export interface CollabAvatarData {
    userId: string;
    userName?: string;
    userColor?: string;
    cameraPosition: [number, number, number];
    cameraTarget: [number, number, number];
    cameraUp?: [number, number, number];
    isActive: boolean;
    timestamp: number;
}

export interface AvatarUpdateData {
    roomId: string;
    userId: string;
    userName: string;
    userColor: string;
    cameraPosition: [number, number, number];
    cameraTarget: [number, number, number];
    cameraUp: [number, number, number];
    isActive: boolean;
}

// ============================================================================
// Selection Types
// ============================================================================

export interface CollabSelectionData {
    userId: string;
    userName?: string;
    userColor?: string;
    selectedUuids: string[];
    timestamp: number;
}

export interface SelectionChangedData {
    roomId: string;
    userId: string;
    userName: string;
    userColor: string;
    selectedUuids: string[];
    selectionMode: 'replace' | 'add' | 'remove';
}

// ============================================================================
// Camera Types
// ============================================================================

export interface CollabCameraData {
    userId: string;
    position: [number, number, number];
    target: [number, number, number];
    up?: [number, number, number];
    zoom: number;
    timestamp: number;
}

export interface CameraUpdateData {
    roomId: string;
    userId: string;
    position: [number, number, number];
    target: [number, number, number];
    up: [number, number, number];
    zoom: number;
}

export interface CameraFollowData {
    roomId: string;
    followerId: string;
    targetUserId: string;
}

// ============================================================================
// Markup Types
// ============================================================================

export type MarkupType = 'arrow' | 'line' | 'polyline' | 'circle' | 'text' | 'callout';

export interface CollabMarkupData {
    markupId: string;
    userId: string;
    userName?: string;
    userColor?: string;
    type: MarkupType;
    points: [number, number, number][];
    text?: string;
    fontSize?: number;
    lineWidth?: number;
    arrowSize?: number;
    attachedTo?: string;
    createdAt?: number;
    updatedAt?: number;
}

export interface MarkupAddData {
    roomId: string;
    markupId: string;
    userId: string;
    userName: string;
    userColor: string;
    type: MarkupType;
    points: [number, number, number][];
    text?: string;
    fontSize?: number;
    lineWidth?: number;
    arrowSize?: number;
}

export interface MarkupUpdateData {
    roomId: string;
    markupId: string;
    userId: string;
    points?: [number, number, number][];
    text?: string;
}

export interface MarkupDeleteData {
    roomId: string;
    markupId: string;
    userId: string;
}

export interface MarkupSyncData {
    action: 'add' | 'update' | 'delete';
    markup?: CollabMarkupData;
    markupId?: string;
}

// ============================================================================
// Event Constants
// ============================================================================

export const COLLAB_EVENTS = {
    // Room
    ROOM_JOIN: 'FikoYT.cad.collab.room.join',
    ROOM_JOINED: 'FikoYT.cad.collab.room.joined',
    ROOM_LEAVE: 'FikoYT.cad.collab.room.leave',
    ROOM_USER_JOINED: 'FikoYT.cad.collab.room.user.joined',
    ROOM_USER_LEFT: 'FikoYT.cad.collab.room.user.left',

    // Cursor
    CURSOR_MOVE: 'FikoYT.cad.collab.cursor.move',
    CURSOR_UPDATE: 'FikoYT.cad.collab.cursor.update',
    CURSOR_HIDE: 'FikoYT.cad.collab.cursor.hide',

    // Avatar
    AVATAR_UPDATE: 'FikoYT.cad.collab.avatar.update',
    AVATAR_SYNC: 'FikoYT.cad.collab.avatar.sync',
    AVATAR_IDLE: 'FikoYT.cad.collab.avatar.idle',

    // Selection
    SELECTION_CHANGED: 'FikoYT.cad.collab.selection.changed',
    SELECTION_BROADCAST: 'FikoYT.cad.collab.selection.broadcast',
    SELECTION_CLEAR: 'FikoYT.cad.collab.selection.clear',

    // Camera
    CAMERA_UPDATE: 'FikoYT.cad.collab.camera.update',
    CAMERA_BROADCAST: 'FikoYT.cad.collab.camera.broadcast',
    CAMERA_FOLLOW_START: 'FikoYT.cad.collab.camera.follow.start',
    CAMERA_FOLLOW_STARTED: 'FikoYT.cad.collab.camera.follow.started',
    CAMERA_FOLLOW_STOP: 'FikoYT.cad.collab.camera.follow.stop',
    CAMERA_FOLLOW_STOPPED: 'FikoYT.cad.collab.camera.follow.stopped',

    // Markup
    MARKUP_ADD: 'FikoYT.cad.collab.markup.add',
    MARKUP_UPDATE: 'FikoYT.cad.collab.markup.update',
    MARKUP_DELETE: 'FikoYT.cad.collab.markup.delete',
    MARKUP_SYNC: 'FikoYT.cad.collab.markup.sync',

    // Scene Object Sync
    SCENE_OBJECT_TRANSFORM: 'FikoYT.cad.collab.scene.transform',
    SCENE_OBJECT_VISIBILITY: 'FikoYT.cad.collab.scene.visibility',
    SCENE_OBJECT_COLOR: 'FikoYT.cad.collab.scene.color',
    SCENE_OBJECT_DELETE: 'FikoYT.cad.collab.scene.delete',
    SCENE_OBJECT_SYNC: 'FikoYT.cad.collab.scene.sync',

    // File Share
    FILE_SHARE_UPLOAD: 'FikoYT.cad.collab.file.upload',
    FILE_SHARE_READY: 'FikoYT.cad.collab.file.ready',
    FILE_SHARE_REQUEST: 'FikoYT.cad.collab.file.request',
} as const;

// ============================================================================
// Scene Object Sync Types
// ============================================================================

export type SceneOperationType = 'transform' | 'visibility' | 'color' | 'delete';

export interface SceneObjectTransformData {
    roomId: string;
    userId: string;
    objectName: string;
    position?: [number, number, number];
    rotation?: [number, number, number];
    scale?: [number, number, number];
}

export interface SceneObjectVisibilityData {
    roomId: string;
    userId: string;
    objectName: string;
    visible: boolean;
}

export interface SceneObjectColorData {
    roomId: string;
    userId: string;
    objectName: string;
    color: string;         // hex "#FF0000"
    opacity?: number;
}

export interface SceneObjectDeleteData {
    roomId: string;
    userId: string;
    objectName: string;
}

export interface SceneObjectSyncData {
    action: SceneOperationType;
    objectName: string;
    userId: string;
    userName?: string;
    transform?: SceneObjectTransformData;
    visibility?: SceneObjectVisibilityData;
    color?: SceneObjectColorData;
}

// ============================================================================
// File Share Types
// ============================================================================

export interface FileShareUploadData {
    roomId: string;
    userId: string;
    userName: string;
    fileName: string;
    fileSize: number;
    fileType: string;       // 'step' | 'stp' | 'iges' | 'obj' | 'stl'
    fileUrl: string;        // sunucudaki URL
    timestamp: number;
}

export interface FileShareReadyData {
    roomId: string;
    userId: string;
    userName: string;
    fileName: string;
    fileUrl: string;
    fileSize: number;
    fileType: string;
}

export interface FileShareRequestData {
    roomId: string;
    userId: string;
    fileName: string;
}

// ============================================================================
// Configuration
// ============================================================================

export const COLLAB_CONFIG = {
    // Throttle intervals (ms)
    CURSOR_THROTTLE: 50,
    AVATAR_THROTTLE: 100,
    CAMERA_THROTTLE: 100,

    // Lerp factors for smooth interpolation
    CURSOR_LERP: 0.3,
    CAMERA_LERP: 0.1,

    // Timeouts (ms)
    IDLE_TIMEOUT: 30000,  // 30 seconds
    CURSOR_HIDE_DELAY: 3000,  // 3 seconds

    // Visual settings
    CURSOR_RAY_LENGTH: 100,
    AVATAR_SIZE: 0.5,
    HIGHLIGHT_OPACITY: 0.3,
} as const;

// ============================================================================
// Utility Types
// ============================================================================

export type Vec3Tuple = [number, number, number];

export function toVec3Tuple(x: number, y: number, z: number): Vec3Tuple {
    return [x, y, z];
}

export function generateMarkupId(): string {
    return `markup_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
}
